import { memo, useEffect, useRef, useState } from 'react';

import rough from 'roughjs';

import { Text } from '@spotify-internal/encore-web';

import styles from './DrawingCanvas.module.css';

type DrawingOptions = {
  stroke?: string;
  fill?: string;
  strokeWidth?: number;
  roughness?: number;
  bowing?: number;
  fillStyle?: string;
  fillWeight?: number;
  hachureAngle?: number;
  hachureGap?: number;
};

export type DrawingElement =
  | {
      type: 'line';
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      options?: DrawingOptions;
    }
  | {
      type: 'rectangle';
      x: number;
      y: number;
      width: number;
      height: number;
      options?: DrawingOptions;
    }
  | {
      type: 'circle';
      x: number;
      y: number;
      diameter: number;
      options?: DrawingOptions;
    }
  | {
      type: 'ellipse';
      x: number;
      y: number;
      width: number;
      height: number;
      options?: DrawingOptions;
    }
  | {
      type: 'polygon';
      vertices: [number, number][];
      options?: DrawingOptions;
    }
  | {
      type: 'arc';
      x: number;
      y: number;
      width: number;
      height: number;
      start: number;
      stop: number;
      closed?: boolean;
      options?: DrawingOptions;
    }
  | {
      type: 'curve';
      points: [number, number][];
      options?: DrawingOptions;
    }
  | {
      type: 'linearPath';
      points: [number, number][];
      options?: DrawingOptions;
    }
  | { type: 'path'; d: string; options?: DrawingOptions }
  | {
      type: 'text';
      x: number;
      y: number;
      text: string;
      fontSize?: number;
      fontFamily?: string;
      fill?: string;
      textAnchor?: 'start' | 'middle' | 'end';
    };

type Props = {
  width: number;
  height: number;
  elements: DrawingElement[];
  backgroundColor?: string;
};

export const DrawingCanvas = memo(
  ({ width, height, elements, backgroundColor }: Props) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const [drawingFailed, setDrawingFailed] = useState(false);

    useEffect(() => {
      const svg = svgRef.current;
      if (!svg) return;

      while (svg.firstChild) {
        svg.removeChild(svg.firstChild);
      }

      try {
        if (backgroundColor) {
          const bg = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'rect',
          );
          bg.setAttribute('width', String(width));
          bg.setAttribute('height', String(height));
          bg.setAttribute('fill', backgroundColor);
          svg.appendChild(bg);
        }

        const rc = rough.svg(svg);

        for (const el of elements) {
          let node: SVGGElement | undefined;
          switch (el.type) {
            case 'line':
              node = rc.line(el.x1, el.y1, el.x2, el.y2, el.options);
              break;
            case 'rectangle':
              node = rc.rectangle(el.x, el.y, el.width, el.height, el.options);
              break;
            case 'circle':
              node = rc.circle(el.x, el.y, el.diameter, el.options);
              break;
            case 'ellipse':
              node = rc.ellipse(el.x, el.y, el.width, el.height, el.options);
              break;
            case 'polygon':
              node = rc.polygon(el.vertices, el.options);
              break;
            case 'arc':
              node = rc.arc(
                el.x,
                el.y,
                el.width,
                el.height,
                el.start,
                el.stop,
                el.closed,
                el.options,
              );
              break;
            case 'curve':
              node = rc.curve(el.points, el.options);
              break;
            case 'linearPath':
              node = rc.linearPath(el.points, el.options);
              break;
            case 'path':
              node = rc.path(el.d, el.options);
              break;
            case 'text': {
              const textEl = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'text',
              );
              textEl.setAttribute('x', String(el.x));
              textEl.setAttribute('y', String(el.y));
              textEl.setAttribute('fill', el.fill ?? 'black');
              textEl.setAttribute('font-size', String(el.fontSize ?? 16));
              textEl.setAttribute('font-family', el.fontFamily ?? 'sans-serif');
              if (el.textAnchor) {
                textEl.setAttribute('text-anchor', el.textAnchor);
              }
              textEl.textContent = el.text;
              svg.appendChild(textEl);
              break;
            }
            default:
              break;
          }
          if (node) {
            svg.appendChild(node);
          }
        }

        setDrawingFailed(false);
      } catch {
        while (svg.firstChild) {
          svg.removeChild(svg.firstChild);
        }
        setDrawingFailed(true);
      }
    }, [backgroundColor, elements, height, width]);

    return (
      <div className={styles.root}>
        {drawingFailed && (
          <div className={styles.error}>
            <Text variant="bodySmall">Drawing could not be rendered</Text>
          </div>
        )}
        <svg
          ref={svgRef}
          className={styles.svg}
          style={{ display: drawingFailed ? 'none' : undefined }}
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
        />
      </div>
    );
  },
);
DrawingCanvas.displayName = 'DrawingCanvas';
